import { useTranslation } from 'react-i18next';

import type { RecognitionAppreciationCard } from '@bonusly/bizy-kit';
import { Box, Flex, Text } from '@bonusly/design-system';

import { AmountPill, HashtagToken, RecipientHandle } from './lib/recognition-segments';
import { UiBlockActionBar } from './ui-block-action-bar';

/**
 * Pollinator mode card.
 *
 * Shows a snapshot of a real recognition post or celebration the user
 * hasn't engaged with yet, then offers one-tap appreciation:
 *
 *   CELEBRATION                            (kind === 'celebration' only)
 *   Giver Name: @recipient +10
 *   [message body #hashtag]
 *   Apr 16, 2026
 *                          [Add on] [Comment] [View post]
 *
 * The snapshot reuses the same segments as the recognition view / draft
 * cards so the post reads identically across surfaces. Actions are
 * supplied by the backend: "Add on" / "Comment" are `request_draft`
 * actions that hand the post off to Bizy to draft an editable reply
 * (never auto-posted), plus a `view_post` link. This renderer only
 * displays them via {@link UiBlockActionBar}.
 */
export function RecognitionAppreciationCardView({
	block,
	fontSize = 'sm',
	isReadOnly = false,
}: {
	block: RecognitionAppreciationCard;
	fontSize?: 'sm' | 'md';
	isReadOnly?: boolean;
}) {
	const { t } = useTranslation('admin_bizy');
	const { kind, giver, recipient, amount, message, hashtags, date, actions } = block.payload;
	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const hashtagList = (hashtags ?? []).map((h) => h.replace(/^#/, ''));
	const hasHeader = !!giver || recipients.length > 0 || (amount != null && amount > 0);

	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			{kind === 'celebration' && (
				<Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="text.softer" mb={1}>
					{t('uiBlocks.recognitionAppreciation.celebrationLabel')}
				</Text>
			)}
			{hasHeader && (
				<Text fontSize={fontSize} lineHeight="tall">
					{giver && (
						<Text as="span" fontSize="inherit" fontWeight="semibold">
							{giver}:{' '}
						</Text>
					)}
					{recipients.map((r, i) => (
						<span key={`${r}-${i}`}>
							<RecipientHandle username={r} />{' '}
						</span>
					))}
					{amount != null && amount > 0 && <AmountPill amount={amount} />}
				</Text>
			)}
			<Text fontSize={fontSize} mt={hasHeader ? 1 : 0} whiteSpace="pre-wrap">
				{message}
				{hashtagList.length > 0 && (
					<>
						{' '}
						{hashtagList.map((h, i) => (
							<span key={`${h}-${i}`}>
								<HashtagToken hashtag={h} />
								{i < hashtagList.length - 1 ? ' ' : ''}
							</span>
						))}
					</>
				)}
			</Text>
			{date && (
				<Flex mt={2}>
					<Text fontSize="xs" color="text.softer">
						{date}
					</Text>
				</Flex>
			)}
			<UiBlockActionBar actions={actions} areActionsDisabled={isReadOnly} />
		</Box>
	);
}
